import React, { useState, useEffect } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import {
  Home,
  BarChart3,
  Package,
  CheckCircle,
  Building2,
  Users,
  AlertTriangle,
  Calendar,
} from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { API } from "../utils/constants";
import TechnicianDashboard from "../components/technician/TechnicianDashboard";
import ServiceProviderDashboard from "../components/service-provider/ServiceProviderDashboard";
import AnalyticsHub from "../components/analytics/AnalyticsHub";

const DashboardPage = () => {
  const { user } = useAuth();

  if (user?.role === 'Technician' || user?.role === 'Maintenance Staff') {
    return <TechnicianDashboard />;
  }

  if (user?.role === 'Service Provider') {
    return <ServiceProviderDashboard />;
  }

  const [activeTab, setActiveTab] = useState('overview');
  const [dashboardStats, setDashboardStats] = useState({
    total_assets: 0,
    active_assets: 0,
    total_properties: 0,
    total_users: 0,
    total_service_logs: 0,
    pending_incidents: 0,
    recent_logins: 0
  });
  const [recentActivities, setRecentActivities] = useState([]);
  const [statsLoading, setStatsLoading] = useState(true);
  const [activitiesLoading, setActivitiesLoading] = useState(true);
  const [statsError, setStatsError] = useState(null);
  const [activitiesError, setActivitiesError] = useState(null);

  const fetchDashboardStats = async () => {
    try {
      setStatsLoading(true);
      setStatsError(null);
      const response = await axios.get(`${API}/dashboard/stats`);
      setDashboardStats(response.data);
    } catch (error) {
      console.error("Error fetching dashboard stats:", error);
      setStatsError("Failed to load dashboard statistics");
      toast.error("Failed to load dashboard statistics");
    } finally {
      setStatsLoading(false);
    }
  };

  const fetchRecentActivities = async () => {
    try {
      setActivitiesLoading(true);
      setActivitiesError(null);
      const response = await axios.get(`${API}/dashboard/activities?limit=8`);
      setRecentActivities(response.data.activities || []);
    } catch (error) {
      console.error("Error fetching recent activities:", error);
      setActivitiesError("Failed to load recent activities");
      toast.error("Failed to load recent activities");
    } finally {
      setActivitiesLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardStats();
    fetchRecentActivities();
  }, []);

  const formatRelativeTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMilliseconds = now - date;
    const diffInMinutes = Math.floor(diffInMilliseconds / (1000 * 60));
    const diffInHours = Math.floor(diffInMinutes / 60);
    const diffInDays = Math.floor(diffInHours / 24);

    if (diffInMinutes < 1) return "Just now";
    if (diffInMinutes < 60) return `${diffInMinutes} minute${diffInMinutes !== 1 ? 's' : ''} ago`;
    if (diffInHours < 24) return `${diffInHours} hour${diffInHours !== 1 ? 's' : ''} ago`;
    if (diffInDays < 7) return `${diffInDays} day${diffInDays !== 1 ? 's' : ''} ago`;
    return date.toLocaleDateString();
  };

  const getActivityColor = (activityType) => {
    const colorMap = {
      'user_login': '#00C5CB',
      'user_logout': '#6B7280',
      'user_register': '#10B981',
      'asset_create': '#00C5CB',
      'asset_update': '#F59E0B',
      'asset_delete': '#EF4444',
      'property_create': '#8B5CF6',
      'property_update': '#F59E0B',
      'service_log_create': '#3B82F6',
      'incident_create': '#EF4444',
      'system_event': '#6B7280'
    };
    return colorMap[activityType] || '#6B7280';
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
        <div className="text-sm text-gray-500">
          Welcome back, {user.name}
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('overview')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'overview'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Home className="inline-block w-4 h-4 mr-2" />
            Overview
          </button>
          <button
            onClick={() => setActiveTab('analytics')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'analytics'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <BarChart3 className="inline-block w-4 h-4 mr-2" />
            Analytics
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' ? (
        <div className="space-y-6">

          {/* Stats Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Total Assets</p>
                  {statsLoading ? (
                    <div className="animate-pulse">
                      <div className="h-8 bg-gray-200 rounded w-16 mt-1"></div>
                    </div>
                  ) : statsError ? (
                    <p className="text-2xl font-bold text-red-500">--</p>
                  ) : (
                    <p className="text-2xl font-bold text-gray-900">{dashboardStats.total_assets}</p>
                  )}
                </div>
                <Package className="h-8 w-8" style={{ color: '#00C5CB' }} />
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Active Assets</p>
                  {statsLoading ? (
                    <div className="animate-pulse">
                      <div className="h-8 bg-gray-200 rounded w-16 mt-1"></div>
                    </div>
                  ) : statsError ? (
                    <p className="text-2xl font-bold text-red-500">--</p>
                  ) : (
                    <p className="text-2xl font-bold text-gray-900">{dashboardStats.active_assets}</p>
                  )}
                </div>
                <CheckCircle className="h-8 w-8 text-green-500" />
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Properties</p>
                  {statsLoading ? (
                    <div className="animate-pulse">
                      <div className="h-8 bg-gray-200 rounded w-16 mt-1"></div>
                    </div>
                  ) : statsError ? (
                    <p className="text-2xl font-bold text-red-500">--</p>
                  ) : (
                    <p className="text-2xl font-bold text-gray-900">{dashboardStats.total_properties}</p>
                  )}
                </div>
                <Building2 className="h-8 w-8" style={{ color: '#00C5CB' }} />
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Users</p>
                  {statsLoading ? (
                    <div className="animate-pulse">
                      <div className="h-8 bg-gray-200 rounded w-16 mt-1"></div>
                    </div>
                  ) : statsError ? (
                    <p className="text-2xl font-bold text-red-500">--</p>
                  ) : (
                    <p className="text-2xl font-bold text-gray-900">{dashboardStats.total_users}</p>
                  )}
                </div>
                <Users className="h-8 w-8" style={{ color: '#00C5CB' }} />
              </div>
            </div>
          </div>

          {/* Recent Activity */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Recent Activity</h2>
              <button
                onClick={fetchRecentActivities}
                className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1"
                disabled={activitiesLoading}
              >
                {activitiesLoading ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-400"></div>
                ) : (
                  "Refresh"
                )}
              </button>
            </div>

            {activitiesLoading ? (
              <div className="space-y-3">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="animate-pulse flex items-center space-x-3">
                    <div className="w-2 h-2 bg-gray-200 rounded-full"></div>
                    <div className="flex-1">
                      <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                    </div>
                    <div className="h-4 bg-gray-200 rounded w-16"></div>
                  </div>
                ))}
              </div>
            ) : activitiesError ? (
              <div className="text-center py-8">
                <AlertTriangle className="mx-auto h-8 w-8 text-red-500 mb-2" />
                <p className="text-gray-500">{activitiesError}</p>
                <button
                  onClick={fetchRecentActivities}
                  className="mt-2 text-sm text-blue-600 hover:text-blue-800"
                >
                  Try again
                </button>
              </div>
            ) : recentActivities.length === 0 ? (
              <div className="text-center py-8">
                <Calendar className="mx-auto h-8 w-8 text-gray-400 mb-2" />
                <p className="text-gray-500">No recent activity</p>
              </div>
            ) : (
              <div className="space-y-3">
                {recentActivities.map((activity, index) => (
                  <div key={activity.id || index} className="flex items-center space-x-3 text-sm">
                    <div
                      className="w-2 h-2 rounded-full"
                      style={{ backgroundColor: getActivityColor(activity.activity_type) }}
                    ></div>
                    <span className="text-gray-600 flex-1">{activity.description}</span>
                    <span className="text-gray-400">
                      {formatRelativeTime(activity.created_at)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      ) : (
        <AnalyticsHub />
      )}
    </div>
  );
};

export default DashboardPage;
